<template>
  <div class="row">
    <!-- Flash banner (fixed overlay, no layout shift) -->
    <transition name="flash">
      <div
        v-if="flash.show"
        class="flash-banner-fixed"
        :class="flashClass"
        role="status"
        aria-live="polite"
      >
        {{ flash.message }}
      </div>
    </transition>

    <div
      class="auth-viewport vh-100 align-items-center d-flex bg-white rounded-3 overflow-hidden"
    >
      <div class="card shadow-none border-0 ms-auto me-auto login-card w-100">
        <div class="card-body rounded-0 text-left p-3">
          <h2 class="fw-700 display1-size display2-md-size mb-4">
            Create A New Account
          </h2>

          <form @submit.prevent="onSubmit" novalidate>
            <!-- HANDLE -->
            <div class="form-group icon-input mt-3">
              <i class="font-sm ti-user text-theme-2 pe-0"></i>
              <input
                type="text"
                name="handle"
                id="regHandle"
                class="style2-input ps-5 form-control text-theme-2 font-xsss fw-600"
                :class="{ 'invalid-input': showErr('handle') }"
                placeholder="Your Username"
                v-model.trim="form.handle"
                @blur="touch('handle')"
                autocomplete="username"
                aria-describedby="handleHelp handleError"
                aria-invalid="true"
              />
            </div>
            <small id="handleHelp" class="text-theme-1 d-block mb-1">
              Username must be 3–30 characters.
            </small>
            <p v-if="showErr('handle')" id="handleError" class="field-error">
              {{ errors.handle }}
            </p>

            <!-- EMAIL -->
            <div class="form-group icon-input mt-3">
              <i class="font-sm ti-email text-theme-2 pe-0"></i>
              <input
                type="email"
                name="email"
                id="regEmail"
                class="style2-input ps-5 form-control text-theme-2 font-xsss fw-600"
                :class="{ 'invalid-input': showErr('email') }"
                placeholder="Your Email Address"
                v-model.trim="form.email"
                @blur="touch('email')"
                autocomplete="email"
                aria-describedby="emailHelp emailError"
                aria-invalid="true"
              />
            </div>
            <small id="emailHelp" class="text-theme-1 d-block mb-1">
              We’ll never share your email with anyone else.
            </small>
            <p v-if="showErr('email')" id="emailError" class="field-error">
              {{ errors.email }}
            </p>

            <!-- PASSWORD -->
            <div class="form-group icon-input mt-3">
              <input
                type="password"
                name="password"
                id="regPassword"
                class="style2-input ps-5 form-control text-theme-2 font-xss ls-3"
                :class="{ 'invalid-input': showErr('password') }"
                placeholder="Password"
                v-model="form.password"
                @blur="touch('password')"
                autocomplete="new-password"
                aria-describedby="passwordHelp passwordError"
                aria-invalid="true"
              />
              <i class="font-sm ti-lock text-theme-2 pe-0"></i>
            </div>
            <small id="passwordHelp" class="text-theme-1 d-block mb-1">
              Minimum <strong>8</strong> characters.
            </small>
            <p
              v-if="showErr('password')"
              id="passwordError"
              class="field-error"
            >
              {{ errors.password }}
            </p>

            <!-- CONFIRM PASSWORD -->
            <div class="form-group icon-input mt-3">
              <input
                type="password"
                name="confirm_password"
                id="regPassword2"
                class="style2-input ps-5 form-control text-theme-2 font-xss ls-3"
                :class="{ 'invalid-input': showErr('confirm') }"
                placeholder="Confirm Password"
                v-model="form.confirm"
                @blur="touch('confirm')"
                autocomplete="new-password"
                aria-describedby="confirmError"
                aria-invalid="true"
              />
              <i class="font-sm ti-lock text-theme-2 pe-0"></i>
            </div>
            <p v-if="showErr('confirm')" id="confirmError" class="field-error">
              {{ errors.confirm }}
            </p>

            <!-- TOS -->
            <div class="form-check text-left mb-3 mt-3">
              <input
                type="checkbox"
                class="form-check-input mt-2"
                id="tos"
                v-model="form.tos"
                @blur="touch('tos')"
              />
              <label class="form-check-label font-xsss text-theme-2" for="tos">
                Accept Terms and Conditions
              </label>
            </div>
            <p v-if="showErr('tos')" class="field-error">
              {{ errors.tos }}
            </p>

            <!-- GLOBAL INLINE ERROR (fallback) -->
            <p v-if="submitError" class="field-error mb-2">{{ submitError }}</p>

            <div class="col-sm-12 p-0 text-left">
              <div class="form-group mb-1">
                <button
                  type="submit"
                  class="form-control text-center style2-input text-white fw-600 bg-dark border-0 p-0"
                  :disabled="loading"
                >
                  <span v-if="loading">Please wait…</span>
                  <span v-else>Register</span>
                </button>
              </div>
              <h6 class="text-grey-500 font-xsss fw-500 mt-0 mb-0 lh-32">
                Already have an account?
                <router-link :to="{ name: 'login' }" class="fw-700 ms-1"
                  >Login</router-link
                >
              </h6>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import http from "@/lib/http"; // axios instance using VUE_APP_API_BASE_URL

const router = useRouter();

/** Local state */
const form = reactive({
  handle: "",
  email: "",
  password: "",
  confirm: "",
  tos: false,
});
const touched = reactive({
  handle: false,
  email: false,
  password: false,
  confirm: false,
  tos: false,
});
const errors = reactive({
  handle: "",
  email: "",
  password: "",
  confirm: "",
  tos: "",
});

const loading = ref(false);
const submitError = ref("");

/** Flash banner (with auto-hide) */
const flash = reactive({ show: false, type: "info", message: "" });
const flashClass = computed(() => ({
  "flash-banner": true,
  "flash-success": flash.type === "success",
  "flash-error": flash.type === "error",
  "flash-info": flash.type === "info",
}));

let hideTimer = null;
function showFlash(type, message) {
  flash.type = type;
  flash.message = message;
  flash.show = true;
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    flash.show = false;
  }, 3000);
}
onBeforeUnmount(() => hideTimer && clearTimeout(hideTimer));

/** Validation (mirror backend) */
const handleRegex = /^[a-zA-Z0-9_]{3,30}$/; // server: 3-30
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const minPassword = 8; // server: >= 8

function touch(field) {
  touched[field] = true;
  validateField(field);
}
function showErr(field) {
  return touched[field] && !!errors[field];
}
function validateField(field) {
  switch (field) {
    case "handle":
      if (!form.handle) errors.handle = "Username is required.";
      else if (!handleRegex.test(form.handle))
        errors.handle = "Use 3–30 letters, numbers, or underscores.";
      else errors.handle = "";
      break;
    case "email":
      if (!form.email) errors.email = "Email is required.";
      else if (!emailRegex.test(form.email))
        errors.email = "Enter a valid email address.";
      else errors.email = "";
      break;
    case "password":
      if (!form.password) errors.password = "Password is required.";
      else if (form.password.length < minPassword)
        errors.password = `Password must be at least ${minPassword} characters.`;
      else errors.password = "";
      validateField("confirm");
      break;
    case "confirm":
      if (!form.confirm) errors.confirm = "Please confirm your password.";
      else if (form.confirm !== form.password)
        errors.confirm = "Passwords do not match.";
      else errors.confirm = "";
      break;
    case "tos":
      errors.tos = form.tos ? "" : "Please accept Terms and Conditions.";
      break;
  }
}
function validateAll() {
  ["handle", "email", "password", "confirm", "tos"].forEach((f) => {
    touched[f] = true;
    validateField(f);
  });
  return (
    !errors.handle &&
    !errors.email &&
    !errors.password &&
    !errors.confirm &&
    !errors.tos
  );
}

/** Submit -> POST /auth/register (no localStorage writes; redirect only) */
async function onSubmit() {
  submitError.value = "";
  const ok = validateAll();
  if (!ok) {
    submitError.value = "Please fix the highlighted fields.";
    showFlash("error", submitError.value);
    return;
  }

  loading.value = true;
  try {
    const payload = {
      email: form.email.trim().toLowerCase(),
      handle: form.handle.trim().toLowerCase(),
      password: form.password,
    };

    const { data } = await http.post("/auth/register", payload);

    // backend returns { user, tokens } (wrapped or direct). We don't store them here.
    const body = data && (data.user || data.tokens) ? data : data?.data;
    if (!body?.user) {
      throw new Error("Unexpected response from server.");
    }

    // Redirect to login with a query flag; we'll show a banner there later.
    router.replace({ name: "login", query: { registered: "1" } });
  } catch (e) {
    const status = e?.response?.status;
    const msg =
      (e?.response?.data &&
        (e.response.data.message || e.response.data.error)) ||
      e?.message ||
      "Something went wrong.";

    if (status === 409) {
      if (msg.includes("email")) {
        touched.email = true;
        errors.email = "Email already in use.";
        showFlash("error", "Email already in use.");
      } else if (msg.includes("handle")) {
        touched.handle = true;
        errors.handle = "Username already in use.";
        showFlash("error", "Username already in use.");
      } else {
        showFlash("error", "Duplicate entry. Please try different values.");
      }
      return;
    }

    if (status === 400) {
      if (msg.includes("invalid email")) {
        touched.email = true;
        errors.email = "Enter a valid email address.";
      } else if (msg.includes("invalid handle")) {
        touched.handle = true;
        errors.handle = "Use 3–30 letters, numbers, or underscores.";
      } else if (msg.includes("invalid password")) {
        touched.password = true;
        errors.password = `Password must be at least ${minPassword} characters.`;
      }
      showFlash("error", "Please correct the highlighted fields.");
      return;
    }

    showFlash("error", msg || "Server error. Please try again.");
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Enable scrolling when content exceeds viewport */
.auth-viewport {
  height: auto !important;
  min-height: 100vh;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  padding-block: 16px;
}

/* Flash banner (fixed overlay, no layout shift) */
.flash-banner-fixed {
  position: fixed;
  top: 100px; /* keep consistent with LoginView */
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 2000;
  padding: 10px 15px;
  width: min(400px, 90vw);
  pointer-events: none;
  text-align: center;
}
.flash-banner {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.flash-success {
  background: #eafaf1;
  color: #1e7e34;
  border: 1px solid #b6efcc;
}
.flash-error {
  background: #feeeee;
  color: #b00020;
  border: 1px solid #f6bcbc;
}
.flash-info {
  background: #eef5ff;
  color: #1b5dab;
  border: 1px solid #c7dcff;
}

/* Slide-down/up transition */
.flash-enter-from,
.flash-leave-to {
  transform: translate(-50%, -16px);
  opacity: 0;
}
.flash-enter-active,
.flash-leave-active {
  transition: transform 200ms ease, opacity 200ms ease;
}
.flash-enter-to,
.flash-leave-from {
  transform: translate(-50%, 0);
  opacity: 1;
}

/* Validation cues */
.invalid-input {
  border-color: #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.08);
}
.field-error {
  margin: 4px 2px 8px;
  font-size: 12px;
  line-height: 1.3;
  color: #d63031;
}
</style>
